/**
 * Hook da chamada de voz (Fase 2 VoIP — outbound pelo painel).
 * Ciclo: idle → requesting (ticket+mic) → calling (start) → ringing →
 * active (timer) → ended. Encerrar ou fechar o WS derruba a chamada.
 */
import { apiRequest } from '@/shared/lib/api-client';
import { useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';
import { VoipAudio } from './voip-audio';

/** Flag por tenant (toggle no Super). Invalida via tenant.flags_changed. */
export function useVoipEnabled(): boolean {
  const q = useQuery({
    queryKey: ['voip-enabled'],
    queryFn: () => apiRequest<{ enabled: boolean }>('/voip/enabled'),
    staleTime: 300_000,
  });
  return q.data?.enabled ?? false;
}

export type VoipState =
  | 'idle'
  | 'requesting'
  | 'calling'
  | 'ringing'
  | 'active'
  | 'ended'
  | 'error';

interface VoipTicketResponse {
  token: string;
  ws_url: string;
}

const END_LABELS: Record<string, string> = {
  declined: 'Chamada recusada',
  timeout: 'Não atendida',
  busy: 'Ocupado',
  do_not_disturb: 'Não perturbe ativado',
  cancelled: 'Chamada cancelada',
};

export interface VoipCall {
  state: VoipState;
  error: string | null;
  /** Motivo amigável do fim ("Chamada recusada", "Não atendida"...) */
  endLabel: string | null;
  muted: boolean;
  /** Segundos de chamada ativa (timer). */
  elapsed: number;
  start: (ticketId: number) => Promise<void>;
  /** Atende uma chamada ENTRANTE já tocando (Fase 3). */
  answer: (callId: string) => Promise<void>;
  toggleMute: () => void;
  end: () => void;
  reset: () => void;
}

export function useVoipCall(): VoipCall {
  const [state, setState] = useState<VoipState>('idle');
  const [error, setError] = useState<string | null>(null);
  const [endLabel, setEndLabel] = useState<string | null>(null);
  const [muted, setMuted] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const wsRef = useRef<WebSocket | null>(null);
  const audioRef = useRef<VoipAudio | null>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const teardown = useCallback(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = null;
    wsRef.current?.close();
    wsRef.current = null;
    void audioRef.current?.stop();
    audioRef.current = null;
  }, []);

  useEffect(() => teardown, [teardown]);

  const begin = useCallback(
    async (path: string, body: Record<string, unknown>, ringback: boolean) => {
      setState('requesting');
      setError(null);
      setEndLabel(null);
      setMuted(false);
      setElapsed(0);
      try {
        // Mic PRIMEIRO (gesto do usuário) — pedir depois do WS abrir pode
        // estourar o token de 60s se o browser demorar no prompt.
        const audio = new VoipAudio();
        audioRef.current = audio;
        const { token, ws_url } = await apiRequest<VoipTicketResponse>(path, {
          method: 'POST',
          body,
        });

        const ws = new WebSocket(`${ws_url}/voip?token=${token}`);
        ws.binaryType = 'arraybuffer';
        wsRef.current = ws;

        // Handlers ANTES de qualquer await — em localhost o WS abre em ms e
        // o evento 'open' se perdia enquanto o microfone inicializava
        // (ficava preso em "Preparando microfone…").
        const audioReady = audio.start((frame) => {
          if (ws.readyState === WebSocket.OPEN) ws.send(frame);
        });

        ws.onopen = () => {
          void audioReady.then(() => {
            setState('calling');
            ws.send(JSON.stringify({ type: 'start' }));
            // Ringback desde o início da tentativa — a lib nem sempre reporta
            // o estado 'ringing' (logs reais: connecting → active direto).
            // Atender chamada entrante não tem ringback (somos o chamado).
            if (ringback) audio.startRingback();
          });
        };
        ws.onmessage = (evt) => {
          if (evt.data instanceof ArrayBuffer) {
            audio.play(evt.data);
            return;
          }
          const msg = JSON.parse(String(evt.data)) as {
            type: string;
            state?: string;
            reason?: string | null;
            code?: string;
            message?: string;
          };
          if (msg.type === 'state') {
            if (msg.state === 'ringing') {
              setState('ringing');
              audio.startRingback(); // tom de chamando (tuuu… tuuu)
            } else if (msg.state === 'active') {
              audio.stopRingback();
              setState('active');
              if (!timerRef.current) {
                timerRef.current = setInterval(() => setElapsed((s) => s + 1), 1000);
              }
            } else if (msg.state === 'ended') {
              if (msg.reason && END_LABELS[msg.reason]) setEndLabel(END_LABELS[msg.reason] ?? null);
              setState('ended');
              teardown();
            }
          } else if (msg.type === 'error') {
            setError(msg.message ?? msg.code ?? 'Erro na chamada');
            setState('error');
            teardown();
          }
        };
        ws.onerror = () => {
          setError('Conexão de áudio falhou');
          setState('error');
          teardown();
        };
        ws.onclose = () => {
          setState((s) => (s === 'active' || s === 'ringing' || s === 'calling' ? 'ended' : s));
          if (timerRef.current) clearInterval(timerRef.current);
        };
        // Mic negado/indisponível → derruba tudo com erro claro.
        await audioReady.catch((err) => {
          throw new Error(
            err instanceof Error && err.name === 'NotAllowedError'
              ? 'Permissão de microfone negada'
              : 'Não foi possível acessar o microfone',
          );
        });
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Não foi possível iniciar a chamada');
        setState('error');
        teardown();
      }
    },
    [teardown],
  );

  const start = useCallback(
    (ticketId: number) => begin('/voip/ticket', { ticket_id: ticketId }, true),
    [begin],
  );
  const answer = useCallback(
    (callId: string) => begin('/voip/answer', { call_id: callId }, false),
    [begin],
  );

  const toggleMute = useCallback(() => {
    setMuted((m) => {
      const next = !m;
      wsRef.current?.send(JSON.stringify({ type: 'mute', muted: next }));
      return next;
    });
  }, []);

  const end = useCallback(() => {
    wsRef.current?.send(JSON.stringify({ type: 'end' }));
    setState('ended');
    teardown();
  }, [teardown]);

  const reset = useCallback(() => {
    teardown();
    setState('idle');
    setError(null);
    setEndLabel(null);
    setElapsed(0);
  }, [teardown]);

  return { state, error, endLabel, muted, elapsed, start, answer, toggleMute, end, reset };
}
